// Mission Impact vs Spend scatter — Recharts.
// data: [{ id, name, spend, impact, healthTone? }]

import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, ZAxis } from 'recharts';
import './charts.css';
import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';
import { useChartContainerWidth } from './useChartContainerWidth.js';

const TONE_COLOR = {
  green: '#5a8c66',
  sage: '#5a8c66',
  amber: '#c08828',
  red: '#c0524d',
  brick: '#c0524d',
  blue: '#1961a1',
  grey: '#5a5e66',
};

export function MissionImpactScatter({ data = [], height = 280, onSelect }) {
  const tooltip = useOptionalRichTooltipController();
  const { ref: chartRef, width: chartWidth } = useChartContainerWidth();
  const points = data.map((d) => ({ ...d, fill: TONE_COLOR[d.healthTone] || '#1961a1' }));
  return (
    <div
      className="scatter"
      role="group"
      aria-label="Mission impact vs spend scatter"
      ref={chartRef}
    >
      {chartWidth > 0 ? (
        <ScatterChart
          width={chartWidth}
          height={height}
          margin={{ top: 16, right: 16, bottom: 32, left: 16 }}
        >
          <CartesianGrid stroke="#e1e4ea" />
          <XAxis
            type="number"
            dataKey="spend"
            name="Spend"
            tick={{ fontSize: 11 }}
            stroke="#5a5e66"
            axisLine={{ stroke: '#c4c7cf' }}
            tickLine={false}
            label={{
              value: 'Spend (USD M)',
              position: 'bottom',
              offset: 8,
              fill: '#5a5e66',
              fontSize: 11,
            }}
          />
          <YAxis
            type="number"
            dataKey="impact"
            name="Mission impact"
            tick={{ fontSize: 11 }}
            stroke="#5a5e66"
            axisLine={false}
            tickLine={false}
            label={{
              value: 'Mission impact',
              angle: -90,
              position: 'insideLeft',
              fill: '#5a5e66',
              fontSize: 11,
              offset: 12,
            }}
          />
          <ZAxis type="number" dataKey="size" range={[80, 320]} />
          <Scatter
            data={points}
            shape={(props) => {
              const point = props.payload;
              const radius = Math.max(8, Math.min(18, Math.sqrt(Number(point?.size || 80)) / 1.8));
              const triggerProps =
                point?.richTooltip && tooltip
                  ? tooltip.getTriggerProps({
                      surfaceId: point.richTooltip.surfaceId,
                      payload: point.richTooltip,
                      testIdPrefix: 'rich-tooltip',
                      ariaLabel: `${point.name} mission impact ${point.impact} at ${point.spend} million planned spend`,
                    })
                  : {
                      onClick: () => onSelect?.(point),
                    };
              return (
                <circle
                  {...triggerProps}
                  className="rich-tooltip-svg-trigger scatter-point-trigger"
                  data-testid={`mission-scatter-point-${point.id}`}
                  cx={props.cx}
                  cy={props.cy}
                  r={radius}
                  fill={point.fill}
                  stroke="rgba(255,255,255,0.92)"
                  strokeWidth={2}
                  tabIndex={point?.richTooltip ? 0 : undefined}
                  role={point?.richTooltip ? 'button' : undefined}
                />
              );
            }}
            isAnimationActive
            animationDuration={600}
          />
        </ScatterChart>
      ) : null}
    </div>
  );
}
